import {Icon} from '@iconify/react';

import React, {CSSProperties, MouseEventHandler, useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {OrderDirection, SearchRankingProduct, SearchRankingsColumnDef, TrackedKeyword} from '../../../util/interfaces';

import {PortalSelectOption, SearchRankingMode} from '../../../util/types';

import PortalSelectController from '../../controls/portalSelect/portalSelectController';

import ProductRow from './productRow';

import Tooltip from '../../controls/tooltip/bluemateTooltip';

import classNames from 'classnames';

import {SearchRankingSort} from '../../../util/enums';

const ROWS_PER_PAGE_OPTIONS: Array<PortalSelectOption<number>> = [
    {value: 5, label: '5'},
    {value: 10, label: '10'},
    {value: 25, label: '25'},
    {value: 50, label: '50'}
];

interface Props {
    products: Array<SearchRankingProduct>;
    loading: boolean;
    rankingMode: SearchRankingMode;
    expandedProductId: string | null;
    onToggleExpand: (ean: string) => void;
    sortField: SearchRankingSort | null;
    sortOrder: OrderDirection;
    onSort: (field: SearchRankingSort) => void;
    page: number;
    limit: number;
    totalRows: number;
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
    accountId: string;
    accountIds: Array<string>;
    countryCodes: Array<'NL' | 'BE'>;
    onAddKeyword: (
        keyword: string,
        ean: string,
        callback: (keyword: TrackedKeyword, restored: boolean) => void
    ) => void;
    columns: Array<SearchRankingsColumnDef>;
    onImportClick: MouseEventHandler;
    onKeywordsChanged: VoidFunction;
    manualKeywordCount: number;
    manualKeywordLimit: number;
    hasModule: boolean;
}

export default function SearchRankingsTable(props: Props) {
    const {t} = useTranslation();
    const {
        products,
        loading,
        rankingMode,
        expandedProductId,
        onToggleExpand,
        sortField,
        sortOrder,
        onSort,
        page,
        limit,
        totalRows,
        onPageChange,
        onLimitChange,
        accountId,
        accountIds,
        countryCodes,
        onAddKeyword,
        columns,
        onKeywordsChanged,
        manualKeywordCount,
        manualKeywordLimit,
        hasModule
    } = props;

    // Column hover state: -1 = no column, 0 = product, 1+ = dynamic columns
    const [hoveredColIndex, setHoveredColIndex] = useState(-1);

    // Scroll sync between header and body
    const headerRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);

    const handleBodyScroll = useCallback(() => {
        if (headerRef.current && bodyRef.current) {
            headerRef.current.scrollLeft = bodyRef.current.scrollLeft;
        }
    }, []);

    const totalPages = Math.ceil(totalRows / limit);

    const pageNumbers = useMemo(() => {
        const pages: Array<number> = [];
        const maxVisible = 5;
        let start = Math.max(0, page - Math.floor(maxVisible / 2));
        const end = Math.min(totalPages, start + maxVisible);
        if (end - start < maxVisible) {
            start = Math.max(0, end - maxVisible);
        }
        for (let i = start; i < end; i++) {
            pages.push(i);
        }
        return pages;
    }, [page, totalPages]);
    const [hasProducts, hasKeywords] = useMemo(() => {
        if (!products.length) return [false, false];
        for (const product of products) if (product.keyword_count) return [true, true];
        return [true, false];
    }, [products]);
    const bodyTableStyle = useMemo(() => {
        const style: CSSProperties = {};
        if (loading && hasProducts) {
            style.opacity = 0.4;
            style.pointerEvents = 'none';
        }
        style.width = hasKeywords ? 'auto' : '100%';
        return style;
    }, [hasKeywords, loading, hasProducts]);
    const bodyTableBodyStyle: CSSProperties | undefined = useMemo(() => {
        if (hasKeywords) return {width: '100%'};
        return undefined;
    }, [hasKeywords]);

    const renderSortIcon = (field: string) => {
        if (sortField === field) {
            return (
                <Icon
                    icon={sortOrder === OrderDirection.ASC ? 'mdi:arrow-up' : 'mdi:arrow-down'}
                    className='sort-icon'
                    style={{opacity: 1, color: '#2568EF'}}
                />
            );
        }
        return <Icon icon='mdi:arrow-up-down' className='sort-icon' />;
    };

    const handleLimitSelect = useCallback(
        (value: number | null) => {
            onLimitChange(value ?? 25);
        },
        [onLimitChange]
    );

    const colCount = 1 + columns.length;
    const showPlaceholder = loading && !hasProducts;
    const showEmpty = !loading && !hasProducts;

    const firstRow = page * limit + 1;
    const lastRow = Math.min((page + 1) * limit, totalRows);

    // Refs for column width sync
    const headerTableRef = useRef<HTMLTableElement>(null);
    const headerColGroupRef = useRef<HTMLTableColElement>(null);
    const bodyTableRef = useRef<HTMLTableElement>(null);
    const bodyColGroupRef = useRef<HTMLTableColElement>(null);

    // Sync column widths: max(header, body) applied to both tables
    useLayoutEffect(() => {
        const headerTable = headerTableRef.current;
        const bodyTable = bodyTableRef.current;
        const headerCols = headerColGroupRef.current;
        const bodyCols = bodyColGroupRef.current;
        const body = bodyRef.current;
        const header = headerRef.current;
        if (!headerTable || !bodyTable || !headerCols || !bodyCols || !body || !header) return;

        const syncWidths = () => {
            const bodyRow = bodyTable.querySelector('tr.product-row') as HTMLTableRowElement | null;
            if (!bodyRow) return;

            const hCols = headerCols.children;
            const bCols = bodyCols.children;
            const bodyCells = bodyRow.cells;
            const headerCells = headerTable.querySelectorAll('thead th');
            const count = Math.min(bodyCells.length, hCols.length, bCols.length);

            // Hide detail rows during measurement so colspan doesn't distort widths
            const detailRows = bodyTable.querySelectorAll('tr.detail-row');
            detailRows.forEach(r => ((r as HTMLElement).style.display = 'none'));

            // Reset to auto + width:auto so we measure true natural widths
            // (not inflated by width:100% distributing extra space)
            for (let i = 0; i < count; i++) {
                (hCols[i] as HTMLElement).style.width = '';
                (bCols[i] as HTMLElement).style.width = '';
            }
            headerTable.style.tableLayout = 'auto';
            bodyTable.style.tableLayout = 'auto';
            headerTable.style.width = 'auto';
            bodyTable.style.width = 'auto';

            // Measure natural widths, take max(header, body) per column
            const widths: Array<number> = [];
            for (let i = 0; i < count; i++) {
                const bodyW = bodyCells[i].getBoundingClientRect().width;
                const headerW = headerCells[i] ? headerCells[i].getBoundingClientRect().width : 0;
                widths.push(Math.max(bodyW, headerW));
            }

            // Restore detail rows
            detailRows.forEach(r => ((r as HTMLElement).style.display = ''));

            // Group similar columns and unify widths within each group
            // col-metric and col-metric-sm share one group, col-rank is another
            const colGroup = (cls: string) => (cls === 'col-metric' || cls === 'col-metric-sm' ? 'metric' : cls);

            const groupMax = new Map<string, number>();
            for (let i = 0; i < columns.length; i++) {
                const g = colGroup(columns[i].className);
                groupMax.set(g, Math.max(groupMax.get(g) ?? 0, widths[i + 1]));
            }
            for (let i = 0; i < columns.length; i++) {
                const g = colGroup(columns[i].className);
                widths[i + 1] = groupMax.get(g) ?? widths[i + 1];
            }

            // If total content width < container, expand Product to fill
            const containerWidth = body.clientWidth;
            const totalContent = widths.reduce((a, b) => a + b, 0);
            if (totalContent < containerWidth) {
                widths[0] += containerWidth - totalContent;
            }

            // Apply unified widths to both colgroups, switch to fixed layout
            const totalWidth = widths.reduce((a, b) => a + b, 0);
            const totalWidthPx = `${totalWidth}px`;
            for (let i = 0; i < count; i++) {
                const w = `${widths[i]}px`;
                if (hasKeywords) (hCols[i] as HTMLElement).style.width = w;
                else (hCols[i] as HTMLElement).style.minWidth = w;
                (bCols[i] as HTMLElement).style.width = w;
            }
            headerTable.style.width = totalWidthPx;
            bodyTable.style.width = totalWidthPx;
            headerTable.style.tableLayout = 'fixed';
            bodyTable.style.tableLayout = 'fixed';

            // Compensate header padding for body scrollbar width
            const scrollbarWidth = body.offsetWidth - body.clientWidth;
            header.style.paddingRight = `${scrollbarWidth}px`;

            // Expose visible width for detail panel (independent of table width)
            body.style.setProperty('--detail-panel-width', `${containerWidth}px`);
        };

        syncWidths();
        const ro = new ResizeObserver(syncWidths);
        ro.observe(body);
        return () => ro.disconnect();
    }, [products, columns, expandedProductId, hasKeywords]);

    return (
        <div className='search-rankings-table-wrapper'>
            {/* Header  fixed layout driven by body column widths */}
            <div className='search-rankings-table-header' ref={headerRef}>
                <table className='search-rankings-table' ref={headerTableRef}>
                    <colgroup ref={headerColGroupRef}>
                        <col />
                        {columns.map(col => (
                            <col key={col.field} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr onMouseLeave={() => setHoveredColIndex(-1)}>
                            <th
                                className='col-product'
                                onClick={() => onSort(SearchRankingSort.PRODUCT)}
                                onMouseEnter={() => setHoveredColIndex(0)}
                                style={{cursor: 'pointer'}}
                            >
                                <div className='th-content'>
                                    <span>{t('searchRankings.table.productPortfolio', 'Product')}</span>
                                    {renderSortIcon('name')}
                                </div>
                            </th>
                            {columns.map((col, idx) => (
                                <th
                                    key={col.field}
                                    className={`${col.className}${hoveredColIndex === idx + 1 ? ' col-hovered' : ''}`}
                                    onClick={col.sortField ? () => onSort(col.sortField!) : undefined}
                                    onMouseEnter={() => setHoveredColIndex(idx + 1)}
                                    style={col.sortField ? {cursor: 'pointer'} : undefined}
                                >
                                    <div className={classNames('th-content', {centered: col.centered})}>
                                        {!col.tooltip ? (
                                            <span>{col.label}</span>
                                        ) : (
                                            <Tooltip className={classNames({wrap: col.wrapTooltip})} text={col.tooltip}>
                                                <span>{col.label}</span>
                                            </Tooltip>
                                        )}
                                        {col.sortField && renderSortIcon(col.field)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                </table>
            </div>

            {/* Body  vertical + horizontal scroll */}
            <div
                className='search-rankings-table-body'
                ref={bodyRef}
                onScroll={handleBodyScroll}
                style={{position: 'relative'}}
            >
                {loading && hasProducts && (
                    <div className='table-loading-overlay'>
                        <Icon icon='mdi:loading' className='spin' style={{fontSize: '2.5rem', color: '#9CA3AF'}} />
                    </div>
                )}
                <table className='search-rankings-table body' ref={bodyTableRef} style={bodyTableStyle}>
                    <colgroup ref={bodyColGroupRef}>
                        <col />
                        {columns.map(col => (
                            <col key={col.field} />
                        ))}
                    </colgroup>
                    <tbody style={bodyTableBodyStyle}>
                        {showPlaceholder && (
                            <tr className='table-placeholder-row'>
                                <td colSpan={colCount}>
                                    <div className='table-placeholder'>
                                        <Icon
                                            icon='mdi:loading'
                                            className='spin'
                                            style={{fontSize: '2.5rem', color: '#9CA3AF'}}
                                        />
                                    </div>
                                </td>
                            </tr>
                        )}
                        {showEmpty && (
                            <tr className='table-placeholder-row'>
                                <td colSpan={colCount}>
                                    <div className='table-placeholder'>
                                        <Icon icon='mdi:magnify-close' className='empty-icon' />
                                        <div className='empty-title'>
                                            {t('searchRankings.table.emptyTitle', 'Geen producten gevonden')}
                                        </div>
                                        <div className='empty-text'>
                                            {t(
                                                'searchRankings.table.emptyText',
                                                'Pas je filters aan of voeg zoekwoorden toe.'
                                            )}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {hasProducts &&
                            products.map(product => (
                                <ProductRow
                                    key={product.ean}
                                    product={product}
                                    columns={columns}
                                    isExpanded={expandedProductId === product.ean}
                                    onToggleExpand={() => onToggleExpand(product.ean)}
                                    accountId={accountId}
                                    accountIds={accountIds}
                                    countryCodes={countryCodes}
                                    rankingMode={rankingMode}
                                    onAddKeyword={onAddKeyword}
                                    hoveredColIndex={hoveredColIndex}
                                    onColHover={setHoveredColIndex}
                                    onKeywordsChanged={onKeywordsChanged}
                                    manualKeywordCount={manualKeywordCount}
                                    manualKeywordLimit={manualKeywordLimit}
                                    hasModule={hasModule}
                                />
                            ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className='search-rankings-pagination'>
                <div className='pagination-left'>
                    <PortalSelectController
                        onChange={handleLimitSelect}
                        value={limit}
                        items={ROWS_PER_PAGE_OPTIONS}
                        label={t('searchRankings.table.rowsPerPage', 'Rijen per pagina')}
                        placeholder={String(limit)}
                        itemsPosition='top'
                        size='small'
                        shape='rounded'
                    />
                </div>

                <div className='pagination-center'>
                    <span className='pagination-info'>
                        {totalRows > 0
                            ? t('searchRankings.table.paginationInfo', '{{first}}–{{last}} van {{total}}', {
                                  first: firstRow,
                                  last: lastRow,
                                  total: totalRows
                              })
                            : t('searchRankings.table.noResults', '0 resultaten')}
                    </span>
                </div>

                <div className='pagination-right'>
                    <button
                        className='pagination-btn'
                        disabled={page === 0}
                        onClick={() => onPageChange(0)}
                        title={t('searchRankings.table.firstPage', 'Eerste pagina')}
                    >
                        <Icon icon='mdi:chevron-double-left' />
                    </button>
                    <button
                        className='pagination-btn'
                        disabled={page === 0}
                        onClick={() => onPageChange(page - 1)}
                        title={t('searchRankings.table.prevPage', 'Vorige pagina')}
                    >
                        <Icon icon='mdi:chevron-left' />
                    </button>

                    {pageNumbers.map(p => (
                        <button
                            key={p}
                            className={`pagination-btn pagination-page${p === page ? ' active' : ''}`}
                            onClick={() => onPageChange(p)}
                        >
                            {p + 1}
                        </button>
                    ))}

                    <button
                        className='pagination-btn'
                        disabled={page >= totalPages - 1}
                        onClick={() => onPageChange(page + 1)}
                        title={t('searchRankings.table.nextPage', 'Volgende pagina')}
                    >
                        <Icon icon='mdi:chevron-right' />
                    </button>
                    <button
                        className='pagination-btn'
                        disabled={page >= totalPages - 1}
                        onClick={() => onPageChange(totalPages - 1)}
                        title={t('searchRankings.table.lastPage', 'Laatste pagina')}
                    >
                        <Icon icon='mdi:chevron-double-right' />
                    </button>
                </div>
            </div>
        </div>
    );
}
